export const ICF_TITLE_PAGE_TITLE = 'Title Page';

export const ICF_TITLE_PAGE_PROTOCOL_SOURCES = [
  {
    type: 'PROTOCOL',
    titles: [
      '1. PROTOCOL SUMMARY',
      '1.1 Synopsis',
      '1.2 TRIAL SCHEMA',
      '2. INTRODUCTION',
      '4. TRIAL DESIGN',
      '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
      '11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS',
    ],
  },
];

export const ICF_TITLE_PAGE_PROMPT = `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only the Title Page of the ICF.

Requirements

Use clear, professional, participant-friendly language.

The title page should identify the study and the document without overwhelming the participant.

Include only administrative and study identification information appropriate for the title page.

Do not include study procedures, risks, benefits, eligibility criteria, privacy information, or any consent statements.

Do not repeat information that will be explained in Section 1 (Key Information).

Ensure the title page is suitable for global use and can accommodate country- or site-specific information through placeholders where necessary.

Use only information available in the source documents. If information is unavailable, insert clearly labeled placeholders.

Source Documents
Use only the following source materials:

Clinical Protocol
Protocol Synopsis
Sponsor ICF template
Country-specific ICF requirements
Site-specific administrative information (if available)
Protocol amendments
Sponsor document metadata

These documents are the sole source of truth for the title page.

Information Extraction Requirements
Before drafting, identify and extract the following information, where available:

Study Identification
- Full protocol title
- Short study title (if provided)
- Protocol number
- Study phase
- EudraCT number (if applicable)
- EU CT number (if applicable)
- IND number (if participant-facing and applicable)
- ClinicalTrials.gov identifier (NCT number), if participant-facing
- Other country-specific trial registration number(s), if applicable

Sponsor Information
Identify:
- Sponsor name
- Sponsor logo (if applicable, indicate placement only)
- Collaborating organizations (if participant-facing)

Document Information
Identify:
- Document title
- Version number
- Version date
- Amendment number (if applicable)

Site Information
Identify, where available:
- Study site name
- Institution name
- Site number
- Country
- Principal Investigator name

If unavailable, insert placeholders such as:
[Study Site Name]
[Institution Name]
[Site Number]
[Country]
[Principal Investigator Name]

Mandatory Writing Rules

Use the document title: Informed Consent Form

Include a participant-friendly study title.
If the protocol title is excessively technical:
- Retain the official protocol title.
- Include the sponsor-provided short title if available.
- Do not rewrite or simplify the official protocol title.

Include protocol identifiers exactly as provided.
Do not invent registration numbers.
Include sponsor information only if participant-facing.
Include placeholders for missing site-specific information.
Do not include page numbers or document footers unless required by the source materials.
Do not include participant signature fields.
Do not include any study description or summary.

Layout
Generate the title page in the following order:

Document Title
Informed Consent Form

Study Title
Official Protocol Title
If available: Short Study Title

Study Identification
Include as applicable:
- Protocol Number
- Study Phase
- Clinical Trial Registration Number(s)
- EudraCT Number
- EU CT Number
- ClinicalTrials.gov Identifier (NCT Number)
- Other Registration Number(s)
Only include identifiers available in the source materials.

Sponsor
Sponsor: [Sponsor Name]
If applicable: Collaborating Organization(s)

Study Site Information
Include, where available:
- Study Site
- Institution
- Site Number
- Principal Investigator
- Country
If unavailable, insert placeholders.

Document Information
Include:
- Version Number
- Version Date
- Amendment Number (if applicable)

Style Instructions

Keep the title page concise and uncluttered.
Use consistent formatting throughout.
Use clear headings.
Avoid unnecessary explanatory text.
Do not include regulatory language.
Do not include consent statements.
Do not include participant instructions.
Do not include study procedures.
Do not include confidentiality statements.
Do not include contact information (this belongs in Section 13).
Use placeholders for missing administrative information.

OUTPUT FORMAT:
Produce valid HTML suitable for display in a document editor. Use:
- <h1> for "Informed Consent Form"
- <h2> for major section headings (Study Title, Study Identification, Sponsor, Study Site Information, Document Information)
- <h3> for subheadings (Official Study Title, Short Study Title)
- <p> for paragraph text
- <table> with <thead> containing "Item" and "Information" columns and <tbody> rows for Study Identification, Study Site Information, and Document Information tables
- Use placeholders in square brackets for missing data
- Do not include participant copy instructions or consent language`;

export const getICFTitlePageDefinition = () => ({
  title: ICF_TITLE_PAGE_TITLE,
  protocolSources: ICF_TITLE_PAGE_PROTOCOL_SOURCES,
  prompt: ICF_TITLE_PAGE_PROMPT,
  context: '',
});

export const getTitlePageBasePrompt = () => ICF_TITLE_PAGE_PROMPT;

/**
 * Builds the effective LLM prompt: fixed base prompt + optional user update instructions + existing content.
 * The base prompt is never shown in the UI; only updateInstructions come from the blank text box.
 */
export const buildTitlePageEffectivePrompt = (updateInstructions = '', currentOutput = '') => {
  let effective = ICF_TITLE_PAGE_PROMPT;
  const trimmedUpdate = (updateInstructions || '').trim();
  const trimmedOutput = (currentOutput || '').trim();

  if (trimmedUpdate) {
    effective += `\n\n---\nAdditional user instructions (apply on top of all requirements above):\n${trimmedUpdate}`;
  }

  if (trimmedOutput) {
    effective += `\n\n---\nRevise the following existing title page HTML. Apply the user instructions above. Return the complete updated title page in HTML:\n${trimmedOutput}`;
  }

  return effective;
};

export const isICFTitlePageSection = (section) =>
  section?.title === ICF_TITLE_PAGE_TITLE;
